import { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import { useLocation } from '@reach/router';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { DISCORD_INVITE_URL, STORE_URL } from '@/config/constants';

const EASE = [0.32, 0.72, 0, 1] as const;

const Navbar = () => {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const isActive = (path: string) => {
    const current = pathname.replace(/\/$/, '') || '/';
    const target = path.replace(/\/$/, '') || '/';
    return target === '/' ? current === '/' : current.startsWith(target);
  };

  const navLinks = [
    { name: t('navbar.home'), path: '/', external: false },
    { name: t('navbar.rules'), path: '/reglas/', external: false },
    { name: t('navbar.guides'), path: '/guias/', external: false },
    { name: 'Stats', path: 'https://stats.zestian.co/', external: true },
    { name: t('navbar.discord'), path: DISCORD_INVITE_URL, external: true },
  ];

  const linkClass = (active: boolean) =>
    `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
      active ? 'bg-foreground/10 text-foreground' : 'text-foreground-muted hover:bg-foreground/5 hover:text-foreground'
    }`;

  return (
    <header className="pointer-events-none fixed inset-x-0 top-0 z-nav flex justify-center px-4 pt-6">
      <nav
        aria-label="Principal"
        className="pointer-events-auto flex w-full max-w-5xl items-center gap-2 rounded-full border border-foreground/10 bg-background/70 p-2 shadow-[0_8px_32px_rgb(19_18_9/0.6)] backdrop-blur-xl md:w-max"
      >
        <Link to="/" className="flex items-center gap-3 rounded-full py-1 pl-1 pr-3">
          <img src="/images/logo.webp" alt="ZestianMC" className="h-9 w-9 rounded-full object-cover" />
          <span className="flex flex-col leading-none">
            <span className="text-base font-bold tracking-tight text-foreground">{t('navbar.logo.brand')}</span>
            <span className="text-xs font-medium tracking-wide text-foreground-subtle">{t('navbar.logo.network')}</span>
          </span>
        </Link>

        <div className="ml-auto hidden items-center gap-1 md:flex">
          {navLinks.map((link) =>
            link.external ? (
              <a key={link.name} href={link.path} target="_blank" rel="noopener noreferrer" className={linkClass(false)}>
                {link.name}
              </a>
            ) : (
              <Link key={link.name} to={link.path} className={linkClass(isActive(link.path))}>
                {link.name}
              </Link>
            ),
          )}
          <a
            href={STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary-hover active:scale-[0.98]"
          >
            <ShoppingCart size={18} />
            {t('navbar.store')}
          </a>
        </div>

        <button
          type="button"
          aria-label={open ? t('navbar.closeMenu') : t('navbar.openMenu')}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="relative ml-auto flex h-10 w-10 items-center justify-center rounded-full text-foreground md:hidden"
        >
          <span
            className={`absolute h-0.5 w-5 bg-current transition-transform duration-500 ${open ? 'rotate-45' : '-translate-y-1.5'}`}
          />
          <span className={`absolute h-0.5 w-5 bg-current transition-opacity ${open ? 'opacity-0' : ''}`} />
          <span
            className={`absolute h-0.5 w-5 bg-current transition-transform duration-500 ${open ? '-rotate-45' : 'translate-y-1.5'}`}
          />
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="pointer-events-auto fixed inset-0 -z-10 flex flex-col justify-center bg-black/80 px-8 backdrop-blur-3xl md:hidden"
          >
            <ul className="flex flex-col gap-2">
              {[...navLinks, { name: t('navbar.store'), path: STORE_URL, external: true }].map((link, i) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, y: 48 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 24 }}
                  transition={{ duration: 0.7, delay: 0.05 + i * 0.06, ease: EASE }}
                >
                  {link.external ? (
                    <a
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-3 text-4xl font-bold tracking-tight text-foreground-muted hover:text-foreground"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      className={`block py-3 text-4xl font-bold tracking-tight ${isActive(link.path) ? 'text-primary' : 'text-foreground-muted hover:text-foreground'}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
